import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, ArrowRight, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import AppIcon from "@/components/AppIcon";

const KIND_COLOR = {
  need: "bg-red-500", offer: "bg-emerald-600", auction: "bg-blue-600",
  lead: "bg-amber-500", matched: "bg-purple-600", rate: "bg-stone-600",
};

/** P6.4 · Compact "Around you within 20 km" carousel for the Home hero. */
export default function NearbyHomeWidget({ lat, lng }) {
  const [rows, setRows] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof lat !== "number" || typeof lng !== "number") { setReady(true); return; }
    let cancelled = false;
    api.get("/social/nearby", { params: { lat, lng, kind: "all", limit: 6 } })
      .then((r) => {
        if (cancelled) return;
        const payload = r.data?.rows || r.data || [];
        setRows(payload.slice(0, 6));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, [lat, lng]);

  if (!ready) return null;
  if (rows.length === 0) return null;

  return (
    <div className="mt-5" data-testid="nearby-home-widget">
      <div className="flex items-center gap-2 mb-2">
        <Radio size={14} className="text-emerald-600 animate-pulse"/>
        <div className="text-[11px] uppercase tracking-widest font-black text-stone-900">
          Around you · 20 km
        </div>
        <Link to="/live-world/nearby"
              data-testid="nearby-widget-more"
              className="ml-auto text-[11px] font-black text-emerald-700 inline-flex items-center gap-1">
          See all <ArrowRight size={12}/>
        </Link>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-2 px-2 snap-x snap-mandatory">
        {rows.filter((r, i) => rows.findIndex(x => x.kind === r.kind && x.title === r.title) === i).map((r) => {
          const badge = KIND_COLOR[r.status] || KIND_COLOR[r.kind] || KIND_COLOR.rate;
          const href = r.kind === "auction" ? `/live-world/auction/${r.id}` : `/live-world?focus=${r.id}`;
          return (
            <Link key={r.id} to={href} data-testid={`nearby-widget-row-${r.id}`}
                  className="min-w-[220px] max-w-[240px] snap-start bg-white border border-stone-200 rounded-2xl p-2.5 flex gap-2">
              <div className="w-11 h-11 rounded-lg bg-stone-50 grid place-items-center overflow-hidden shrink-0">
                {r.image ? <img src={r.image} alt="" className="w-full h-full object-cover"/>
                          : <AppIcon name={r.category} size={36}/>}
              </div>
              <div className="min-w-0 flex-1">
                <span className={`text-[9px] font-black uppercase text-white inline-block px-1.5 py-0.5 rounded-full ${badge}`}>
                  {r.kind}
                </span>
                <div className="text-[13px] font-black text-stone-900 truncate mt-0.5">{r.title}</div>
                <div className="text-[10px] text-stone-500 font-bold flex items-center gap-1">
                  {r.distance_km != null && <><MapPin size={9}/>{r.distance_km < 0.1 ? "<0.1" : r.distance_km}km</>}
                  {r.kind === "rate" && (r.price != null
                    ? <span className="text-stone-900 ml-1">₹{Number(r.price).toLocaleString("en-IN")}{r.unit} · {r.status === "live" ? "LIVE" : r.status === "delayed" ? "DELAYED" : "UNAVAILABLE"}</span>
                    : <span className="ml-1">UNAVAILABLE</span>)}
                  {r.bid > 0 && <span className="text-red-600 ml-1">₹{Number(r.bid).toLocaleString("en-IN")}</span>}
                  {r.ask > 0 && <span className="text-emerald-700 ml-1">₹{Number(r.ask).toLocaleString("en-IN")}</span>}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
